import React, { useEffect, useState } from 'react';
import { X, Loader2, Languages, Info, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../services/api';

const LANGS = ['fr', 'en', 'de', 'es', 'it'];
const LANG_LABEL = { fr: 'Français', en: 'English', de: 'Deutsch', es: 'Español', it: 'Italiano' };
const FLAG = { fr: 'fr', en: 'gb', de: 'de', es: 'es', it: 'it' };

// Admin modal: proofread every customer e-mail template in the 4 languages before it is ever sent
const AdminEmailTemplatesModal = ({ onClose, onSent }) => {
  const [templates, setTemplates] = useState([]);
  const [kind, setKind] = useState('');
  const [lang, setLang] = useState('fr');
  const [preview, setPreview] = useState(null);
  const [subjects, setSubjects] = useState({});
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const sendMe = async () => {
    setSending(true);
    setSent(null);
    try {
      const r = await api.post(`/admin/emails/preview/${kind}/send`, null, { params: { lang } });
      setSent({ text: `${r.data.status === 'simulated' ? 'Enregistré dans le journal (mode simulation)' : 'Envoyé'} à ${r.data.to} : « ${r.data.subject} »` });
      onSent?.();
    } catch (e) {
      setSent({ error: true, text: e.response?.data?.detail || "Échec de l'envoi" });
    } finally {
      setSending(false);
    }
  };

  useEffect(() => { setSent(null); }, [kind, lang]);

  useEffect(() => {
    api.get('/admin/emails/templates').then((r) => { setTemplates(r.data.templates); setKind(r.data.templates[0]?.kind || ''); });
  }, []);

  useEffect(() => {
    if (!kind) return;
    setLoading(true);
    Promise.all(LANGS.map((l) => api.get(`/admin/emails/preview/${kind}`, { params: { lang: l } }).then((r) => r.data)))
      .then((rows) => {
        setSubjects(Object.fromEntries(rows.map((r) => [r.lang, r])));
      })
      .finally(() => setLoading(false));
  }, [kind]);

  useEffect(() => { setPreview(subjects[lang] || null); }, [subjects, lang]);

  const current = templates.find((t) => t.kind === kind);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden" data-testid="email-templates-modal">
        <div className="p-4 border-b flex items-center justify-between gap-4">
          <div>
            <h2 className="font-bold text-gray-900 flex items-center gap-2"><Languages size={18} className="text-orange-500" /> Aperçu des modèles d'e-mails — 5 langues</h2>
            <p className="text-xs text-gray-500">Rendu avec des données d'exemple (commande fictive OP-20260601-0042). Relisez les textes FR / EN / DE / ES / IT avant tout envoi réel.</p>
          </div>
          <button onClick={onClose} data-testid="email-templates-close" className="text-gray-400 hover:text-gray-700"><X size={22} /></button>
        </div>
        <div className="flex flex-1 min-h-0">
          <aside className="w-64 shrink-0 border-r overflow-y-auto bg-gray-50" data-testid="email-templates-list">
            {templates.map((t) => (
              <button key={t.kind} type="button" onClick={() => setKind(t.kind)} data-testid={`email-template-${t.kind}`}
                className={`w-full text-left px-4 py-3 text-sm border-b border-gray-100 transition-colors ${kind === t.kind ? 'bg-white text-orange-700 font-semibold border-l-4 border-l-orange-500' : 'text-gray-700 hover:bg-white'}`}>
                {t.label}
              </button>
            ))}
          </aside>
          <section className="flex-1 flex flex-col min-w-0">
            <div className="px-4 pt-3 flex flex-wrap items-center gap-2 border-b pb-3">
              {LANGS.map((l) => (
                <button key={l} type="button" onClick={() => setLang(l)} data-testid={`email-template-lang-${l}`}
                  className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border transition-colors ${lang === l ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'}`}>
                  <img src={`https://flagcdn.com/w40/${FLAG[l]}.png`} alt="" width={16} height={12} className="rounded-[2px]" /> {LANG_LABEL[l]}
                </button>
              ))}
              {current && <span className="ml-auto text-xs text-gray-500 inline-flex items-center gap-1"><Info size={12} /> {current.trigger}</span>}
            </div>
            <div className="px-4 py-2 border-b bg-white">
              <p className="text-[11px] uppercase tracking-wide text-gray-400">Objets dans les 5 langues</p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 text-xs text-gray-700" data-testid="email-template-subjects">
                {LANGS.map((l) => <li key={l} className={l === lang ? 'font-semibold text-orange-700' : ''}><span className="uppercase text-gray-400 mr-1">{l}</span>{subjects[l]?.subject || '…'}</li>)}
              </ul>
            </div>
            {loading || !preview ? (
              <div className="flex-1 flex items-center justify-center"><Loader2 className="animate-spin text-orange-500" /></div>
            ) : (
              <>
                <div className="px-4 py-2 border-b flex flex-wrap items-center gap-3">
                  <p className="text-sm font-semibold text-gray-900 flex-1 min-w-0 truncate" data-testid="email-template-subject">{preview.subject}</p>
                  <Button size="sm" onClick={sendMe} disabled={sending} data-testid="email-template-send-me" className="h-8 bg-orange-500 hover:bg-orange-600">
                    {sending ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />} M'envoyer ce modèle ({lang.toUpperCase()})
                  </Button>
                </div>
                {sent && <p data-testid="email-template-sent" className={`px-4 py-1.5 text-xs flex items-center gap-1.5 border-b ${sent.error ? 'text-red-600 bg-red-50' : 'text-green-700 bg-green-50'}`}>{sent.error ? <AlertCircle size={13} /> : <CheckCircle size={13} />} {sent.text}</p>}
                <iframe title="Aperçu modèle" srcDoc={preview.html} className="flex-1 w-full bg-gray-100" sandbox="" data-testid="email-template-frame" />
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default AdminEmailTemplatesModal;
